import { EMPTY_FIELD } from 'constants/emptyField';
import { Resources } from 'constants/resources';

import Skeleton from '@mui/material/Skeleton';
import { useLocalDatagrid } from 'hooks/useLocalDatagrid';
import { useLocalDatagridPagination } from 'hooks/useLocalDatagridPagination';
import { usePermissions } from 'hooks/usePermissions';
import { useRecordModal } from 'hooks/useRecordModal';
import { RefundChargebackReportCommissionAmountField } from 'pages/Finance/RefundChargebackReport/RefundChargebackReportCommissionAmountField/RefundChargebackReportCommissionAmountField';
import { RefundChargebackReportRuleField } from 'pages/Finance/RefundChargebackReport/RefundChargebackReportRuleField/RefundChargebackReportRuleField';
import { RefundChargebackReportTotalAmountField } from 'pages/Finance/RefundChargebackReport/RefundChargebackReportTotalAmountField/RefundChargebackReportTotalAmountField';
import { RaRecord } from 'react-admin';
import { Box } from 'shared/mui/Box';
import { DEFAULT_TIMEZONE } from 'shared/mui/DatePicker/config/defaultValues';
import { TablePagination } from 'shared/mui/Table';
import { Typography } from 'shared/mui/Typography';
import { DatagridSkeleton } from 'shared/react-admin/Datagrids';
import {
  ChipField,
  TextField,
  FunctionField,
  NumberField,
  DateTimeField,
  CopyToClipboardLinkField,
  CopyToClipboardField,
  OrderStatusChipField,
  CurrencyField,
} from 'shared/react-admin/Fields';
import { ReferenceField, ReferenceOneField } from 'shared/react-admin/ReferenceFields';
import { ReferenceLink } from 'shared/react-admin/ReferenceFields/ReferenceLink/ReferenceLink';
import { CommissionShowDialog } from 'shared/widgets/CommissionShowDialog/CommissionShowDialog';
import { MerchantAccountShowDialog } from 'shared/widgets/MerchantAccountShowDialog/MerchantAccountShowDialog';
import { Commission } from 'types/embedded/commission';
import { MerchantAccount } from 'types/embedded/merchantAccount';
import { Method } from 'types/embedded/method';
import { Order } from 'types/embedded/order';
import { RefundChargebackReportOrder } from 'types/embedded/refundChargebackReport';
import { convertReactAdminIdToHydraId, getId } from 'utils';

import {
  BoxStyled,
  DatagridConfigurableStyled,
  ImageFieldStyled,
} from './RefundChargebackReportDatagrid.styled';

type Props = {
  orders: Order[];
  total: number;
  isLoading: boolean;
  timezone?: string;
  empty?: React.ReactElement;
};

export const RefundChargebackReportDatagrid: React.FC<Props> = ({
  total,
  isLoading,
  empty,
  orders = [],
  timezone = DEFAULT_TIMEZONE,
}) => {
  const { hasResourceAccess } = usePermissions();
  const {
    record: merchantAccount,
    handleOpenRecordModal: handleOpenMerchantAccountModal,
    handleCloseRecordModal: handleCloseMerchantAccountModal,
    openRecordModal: openMerchantAccountModal,
  } = useRecordModal<MerchantAccount>();
  const {
    record: commission,
    handleOpenRecordModal: handleOpenCommissionModal,
    handleCloseRecordModal: handleCloseCommissionModal,
    openRecordModal: openCommissionModal,
  } = useRecordModal<Commission>();

  const { sortedData, sort, setSort } = useLocalDatagrid({
    data: orders,
    defaultSort: {
      field: 'createdAt',
      order: 'ASC',
    },
  });

  const { dataPerPage, ...paginationProps } = useLocalDatagridPagination<Order>({
    data: sortedData,
    defaultPage: 0,
    defaultRowsPerPage: 10,
  });

  if (isLoading) {
    return (
      <>
        <BoxStyled>
          <Skeleton height={20} variant="rounded" width={360} />
        </BoxStyled>
        <DatagridSkeleton columnsCount={17} />
      </>
    );
  }

  return (
    <>
      {!!orders?.length && (
        <BoxStyled>
          <Typography variant="h6">Отчёт по рефандам и чарджбекам за период</Typography>
        </BoxStyled>
      )}
      <DatagridConfigurableStyled
        bulkActionButtons={false}
        data={dataPerPage}
        empty={empty}
        resizableColumns={true}
        setSort={setSort}
        sort={sort}
        sx={{ width: '100%' }}
      >
        <CopyToClipboardLinkField
          cellClassName="resize-id"
          getLink={(record: RaRecord) =>
            `/orders/${encodeURIComponent(convertReactAdminIdToHydraId(record.id, Resources.Orders))}/show`
          }
          headerClassName="resize-id"
          label="Kubera ID"
          sortBy="id"
          source="id"
          target="_blank"
        />
        <DateTimeField
          cellClassName="resize-createdAt"
          headerClassName="resize-createdAt"
          label="Создан"
          source="createdAt"
          timezone={timezone}
        />
        <DateTimeField
          cellClassName="resize-updatedAt"
          emptyText={EMPTY_FIELD}
          headerClassName="resize-updatedAt"
          label="Помечен возвратом"
          source="updatedAt"
          timezone={timezone}
        />
        <ReferenceField
          cellClassName="resize-status"
          headerClassName="resize-status"
          label="Статус"
          link={false}
          reference="new/order_statuses"
          source="status.id"
        >
          <OrderStatusChipField iconProps={{ size: 'l' }} source="description" />
        </ReferenceField>
        <FunctionField
          cellClassName="resize-impactToBalance"
          headerClassName="resize-impactToBalance"
          label="Движение по балансу"
          render={(record: RefundChargebackReportOrder) =>
            `${record.impactToBalance ? 'Да' : 'Нет'}`
          }
          source="impactToBalance"
        />
        <NumberField
          cellClassName="resize-amount"
          headerClassName="resize-amount"
          label="Сумма возврата"
          options={{
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }}
          resource="orders"
          source="amount"
        />
        {hasResourceAccess(Resources.Currencies) && (
          <ReferenceOneField
            cellClassName="resize-currency"
            headerClassName="resize-currency"
            label="Валюта"
            link={false}
            reference="currencies"
            resource="currencies"
            sortable={false}
            source="currency.id"
            target="id"
          >
            <CurrencyField alphaCodeSource="alphaCode" nameSource="name" />
          </ReferenceOneField>
        )}
        <RefundChargebackReportCommissionAmountField
          cellClassName="resize-commissionValueByCurrencyOrder"
          headerClassName="resize-commissionValueByCurrencyOrder"
          label="Комиссия по возврату"
          source="commissionValueByCurrencyOrder"
        />
        <RefundChargebackReportCommissionAmountField
          cellClassName="resize-commissionValueByCurrencyCommission"
          headerClassName="resize-commissionValueByCurrencyCommission"
          label="Комиссия по правилу"
          source="commissionValueByCurrencyCommission"
        />
        {hasResourceAccess(Resources.Commissions) && (
          <ReferenceOneField
            cellClassName="resize-commission"
            emptyText={EMPTY_FIELD}
            headerClassName="resize-commission"
            label="Правило, %"
            link={false}
            reference="commissions"
            resource="commissions"
            sortable={false}
            source="commission.id"
            target="id"
          >
            <RefundChargebackReportRuleField
              label="Правило, %"
              onClick={handleOpenCommissionModal}
              source="commission"
            />
          </ReferenceOneField>
        )}
        <FunctionField
          cellClassName="resize-totalAmount"
          headerClassName="resize-totalAmount"
          label="Итого по возврату"
          render={() => {
            return <RefundChargebackReportTotalAmountField source="totalAmount" />;
          }}
          sortable={false}
        />

        <ReferenceOneField
          cellClassName="resize-merchant"
          headerClassName="resize-merchant"
          label="Мерчант"
          link={false}
          reference="merchants"
          resource="orders"
          sortable={false}
          source="merchant.id"
          target="id"
        >
          <ReferenceLink type="show">
            <ChipField source="name" />
          </ReferenceLink>
        </ReferenceOneField>
        <ReferenceOneField
          cellClassName="resize-merchantAccount"
          headerClassName="resize-merchantAccount"
          label="Счет мерчанта"
          link={false}
          reference="merchant_accounts"
          resource="merchant_accounts"
          sortable={false}
          source="merchantAccount.id"
          target="id"
        >
          <FunctionField
            render={(record: MerchantAccount) => (
              <Typography
                color="secondary"
                onClick={() => handleOpenMerchantAccountModal(record)}
                sx={{
                  color: 'primary.main',
                  cursor: 'pointer',
                  textDecoration: 'underline',
                }}
                variant="body2"
              >
                {record?.name}
              </Typography>
            )}
          />
        </ReferenceOneField>
        <ReferenceOneField
          cellClassName="resize-gateway"
          headerClassName="resize-gateway"
          label="Шлюз"
          link={false}
          reference="gateways"
          resource="orders"
          sortable={false}
          source="gateway.id"
          target="id"
        >
          <TextField source="name" />
        </ReferenceOneField>
        <ReferenceOneField
          cellClassName="resize-method"
          emptyText={EMPTY_FIELD}
          headerClassName="resize-method"
          label="Метод"
          link={false}
          reference="methods"
          resource="methods"
          sortable={false}
          source="method.id"
          target="id"
        >
          <FunctionField
            render={(record: Method) => (
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {hasResourceAccess(Resources.MethodLogos) && (
                  <ImageFieldStyled source="logo.filePath" />
                )}
                <Typography variant="body2">{record?.name}</Typography>
              </Box>
            )}
          />
        </ReferenceOneField>
        <CopyToClipboardField
          cellClassName="resize-merchantReference"
          getValue={getId}
          headerClassName="resize-merchantReference"
          isTrimValue
          label="Мерчант ID"
          source="merchantReference"
        />
        <CopyToClipboardField
          cellClassName="resize-providerReferenceId"
          getValue={getId}
          headerClassName="resize-providerReferenceId"
          isTrimValue
          label="Провайдер ID"
          source="providerReferenceId"
        />
      </DatagridConfigurableStyled>
      {total > 0 && (
        <TablePagination
          count={total}
          sx={{ display: 'block', maxHeight: '52px' }}
          {...paginationProps}
        />
      )}
      {merchantAccount && (
        <MerchantAccountShowDialog
          merchantAccount={merchantAccount}
          onClose={handleCloseMerchantAccountModal}
          open={openMerchantAccountModal}
        />
      )}
      {commission && (
        <CommissionShowDialog
          commission={commission}
          onClose={handleCloseCommissionModal}
          open={openCommissionModal}
        />
      )}
    </>
  );
};
